// src/pages/Tab1.tsx
import { logger } from '../utils/logger';
//  ADDED: Swipe gestures for left/right navigation
//  FIX: Prevent flashing during match transitions
//  FIXED: Removed invalid IonGesture import

import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonChip,
  IonActionSheet,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonNote,
  IonList,
  IonListHeader,
  createGesture,
} from '@ionic/react';
import {
  chevronBack,
  chevronForward,
  people,
  ellipsisVertical,
  refreshOutline,
  alertCircleOutline,
  close,
  sparkles,
  calendar,
  logoDiscord,
  locationOutline,
} from 'ionicons/icons';
import './Tab1.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { sendConnectionRequest, recordRejection } from '../services/connectionsService';
import { AvatarPreview } from '../components/avatar/AvatarPreview';
import FindFriends from '../components/FindFriends';
import { ReportModal } from '../components/ReportModal';
import { UserProfileModal } from '../components/UserProfileModal';
import { PremiumPaywall } from '../components/PremiumPaywall';
import { DAYS_OF_WEEK } from '../types/profile';
import { recordSwipe, getSwipeStatus } from '../services/swipeTrackingService';
import { ProfileIncompletePrompt } from '../components/ProfileIncompletePrompt';
import { useProfile } from '../contexts/ProfileContext';
import { useMatchesData } from '../contexts/MatchesDataContext';
import { ProfileCardSkeleton } from '../components/Skeleton';
import { executeWithRetry } from '../services/retryService';
import { handleError, ErrorMessages } from '../services/errorHandler';

const Tab1: React.FC = () => {
  const history = useHistory();
  const cardRef = useRef<HTMLIonCardElement>(null);
  const isSwipingRef = useRef(false); // Track if user is swiping vs clicking

  const { profile, isComplete: profileComplete, loading: profileLoading } = useProfile();
  const {
    matches,
    swipesRemaining,
    isPremium,
    loading: matchesLoading,
    error: matchesError,
    refresh: refreshMatches,
    removeMatch,
    decrementSwipes,
  } = useMatchesData();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [segment, setSegment] = useState<'matches' | 'search'>('matches');
  const [showDiscordBanner, setShowDiscordBanner] = useState(false);

  const currentMatch = matches.length > 0 ? matches[currentIndex] : null;

  // Check if Discord banner should be shown
  useEffect(() => {
    const APP_VERSION = '1.0.0'; // Update this when app version changes
    const BANNER_KEY = 'discord_banner_dismissed';
    const VERSION_KEY = 'discord_banner_version';

    const dismissed = localStorage.getItem(BANNER_KEY);
    const lastVersion = localStorage.getItem(VERSION_KEY);

    // Show banner if never dismissed OR if app version has changed
    if (!dismissed || lastVersion !== APP_VERSION) {
      setShowDiscordBanner(true);
      localStorage.setItem(VERSION_KEY, APP_VERSION);
    }
  }, []);

  const handleDismissDiscordBanner = () => {
    const BANNER_KEY = 'discord_banner_dismissed';
    localStorage.setItem(BANNER_KEY, 'true');
    setShowDiscordBanner(false);
  };

  //  Auto-adjust currentIndex if it goes out of bounds after match removal
  useEffect(() => {
    if (matches.length > 0 && currentIndex >= matches.length) {
      logger.log(' currentIndex out of bounds, adjusting to', matches.length - 1);
      setCurrentIndex(matches.length - 1);
    }
  }, [matches.length, currentIndex]);

  // Define handlers with useCallback before gesture useEffect
  // Navigate forward through matches (chevron button)
  const handleNext = useCallback(() => {
    if (currentIndex < matches.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, matches.length]);

  // Reject/pass on a match (X button)
  const handleReject = useCallback(async () => {
    if (!currentMatch) return;

    const currentMatchId = currentMatch.profile.id;
    const remainingMatches = matches.length - 1; // After removal

    // Remove match at current index (next match slides into place)
    removeMatch(currentIndex);

    // Record rejection and wait for it to complete
    try {
      const result = await executeWithRetry(
        () => recordRejection(currentMatchId),
        3, // 3 retries
        1000 // 1 second initial delay
      );

      if (!result.success) {
        logger.error('Failed to record rejection:', result.error);
      }

      // Refresh if running low on matches (check updated count)
      if (remainingMatches <= 3) {
        logger.log(' Running low on matches, refreshing...');
        // Wait a bit to ensure server has processed the rejection
        setTimeout(() => refreshMatches(), 500);
      }
    } catch (error) {
      const appError = handleError(error, 'record rejection');
      logger.error(appError.userMessage);
    }
  }, [currentMatch, currentIndex, matches.length, removeMatch, refreshMatches]);

  const handleSendRequest = useCallback(async () => {
    if (!currentMatch || sending) return;

    // Check swipe limits for non-premium users
    if (!isPremium) {
      const status = await getSwipeStatus();
      if (status.limitReached && !status.isPremium) {
        setShowPaywall(true);
        return;
      }
    }

    const currentMatchId = currentMatch.profile.id;
    const remainingMatches = matches.length - 1; // After removal

    // Remove match at current index (next match slides into place)
    removeMatch(currentIndex);

    setSending(true);
    try {
      // Send connection request with retry logic
      const result = await executeWithRetry(
        () => sendConnectionRequest(currentMatchId),
        3, // 3 retries
        1000 // 1 second initial delay
      );

      if (result.success) {
        // Record swipe and wait for it to complete
        const swipeResult = await recordSwipe();
        logger.log(' Connection request sent and swipe recorded');

        // Decrement swipe counter immediately
        decrementSwipes();

        // Refresh if running low on matches (check updated count)
        if (remainingMatches <= 3) {
          logger.log(' Running low on matches, refreshing...');
          // Wait a bit to ensure server has processed the connection
          setTimeout(() => refreshMatches(), 500);
        }
      } else {
        logger.error(' Failed to send connection request:', result.error);
        const errorMsg = result.error?.userMessage || ErrorMessages.CONNECTION_REQUEST_FAILED;
        alert(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      logger.error(' Error sending connection request:', error);
      const appError = handleError(error, 'connection request');
      alert(appError.userMessage);
      setShowError(true);
    } finally {
      setTimeout(() => setSending(false), 1000);
    }
  }, [currentMatch, currentIndex, sending, isPremium, matches.length, removeMatch, decrementSwipes, refreshMatches]);

  //  Setup swipe gestures
  useEffect(() => {
    if (!cardRef.current || !currentMatch) return;

    const gesture = createGesture({
      el: cardRef.current,
      threshold: 0,
      gestureName: 'swipe',
      onStart: () => {
        if (cardRef.current) {
          cardRef.current.style.transition = 'none';
        }
        isSwipingRef.current = false; // Reset on gesture start
      },
      onMove: (detail) => {
        if (!cardRef.current) return;

        const deltaX = detail.deltaX;
        const deltaY = detail.deltaY;
        const rotation = deltaX / 20;

        // Consider it a swipe if moved more than 15px in any direction
        if (Math.abs(deltaX) > 15 || Math.abs(deltaY) > 15) {
          isSwipingRef.current = true;
        }

        cardRef.current.style.transform = `translateX(${deltaX}px) rotate(${rotation}deg)`;
        cardRef.current.style.opacity = String(1 - Math.abs(deltaX) / 300);
      },
      onEnd: (detail) => {
        if (!cardRef.current) return;

        cardRef.current.style.transition = 'all 0.3s ease-out';

        const threshold = 100;
        const swipedLeft = detail.deltaX < -threshold;
        const swipedRight = detail.deltaX > threshold;

        if (swipedLeft) {
          // Swipe left = pass/reject
          cardRef.current.style.transform = 'translateX(-500px) rotate(-30deg)';
          cardRef.current.style.opacity = '0';
          setTimeout(() => handleReject(), 300);
        } else if (swipedRight) {
          // Swipe right = connect
          cardRef.current.style.transform = 'translateX(500px) rotate(30deg)';
          cardRef.current.style.opacity = '0';
          setTimeout(() => handleSendRequest(), 300);
        } else {
          // Reset
          cardRef.current.style.transform = '';
          cardRef.current.style.opacity = '1';
        }

        // Reset swiping flag after a short delay (to prevent click from firing)
        setTimeout(() => {
          isSwipingRef.current = false;
        }, 100);
      },
    });

    gesture.enable();

    return () => {
      gesture.destroy();
    };
  }, [currentMatch, currentIndex, matches.length, handleReject, handleSendRequest]);

  const handleRefresh = async (event: CustomEvent) => {
    if (profileComplete) {
      await refreshMatches();
    }
    event.detail.complete();
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleReportComplete = () => {
    removeMatch(currentIndex);
    // Don't change currentIndex - next card shifts into place
    setShowReportModal(false);
  };

  const renderOnboarding = () => {
    return <ProfileIncompletePrompt profile={profile} actionContext="discovering gaming partners near you" />;
  };

  const renderContent = () => {
    if (profileLoading) {
      return (
        <div className="center-content">
          <IonSpinner name="crescent" />
          <IonText color="medium">
            <p className="ion-padding-top">Loading your profile...</p>
          </IonText>
        </div>
      );
    }

    if (!profileComplete) {
      return renderOnboarding();
    }

    //  Show spinner during transition to prevent flash
    if (matchesLoading) {
      return <ProfileCardSkeleton />;
    }

    if (matchesError && !currentMatch) {
      return (
        <div className="center-content">
          <IonIcon icon={alertCircleOutline} style={{ fontSize: '48px' }} color="danger" />
          <IonText color="danger">
            <p className="ion-padding-top">{matchesError}</p>
          </IonText>
          <IonButton onClick={refreshMatches} className="ion-margin-top" fill="outline">
            <IonIcon slot="start" icon={refreshOutline} />
            Try Again
          </IonButton>
        </div>
      );
    }

    if (!currentMatch) {
      return (
        <div className="center-content">
          <IonIcon icon={people} style={{ fontSize: '48px' }} color="medium" />
          <IonText color="medium">
            <p className="ion-padding-top">That's all the matches for now!</p>
            <p>Check back later or refine your profile.</p>
          </IonText>
          <IonButton onClick={refreshMatches} className="ion-margin-top" fill="outline">
            <IonIcon slot="start" icon={refreshOutline} />
            Check Again
          </IonButton>
        </div>
      );
    }

    const { profile, sharedGames, overlappingSlots } = currentMatch;
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === matches.length - 1;

    const getDayName = (dayValue: number) => {
      return DAYS_OF_WEEK.find(d => d.value === dayValue)?.name || `Day ${dayValue}`;
    };

    return (
      <>
        {!isPremium && swipesRemaining >= 0 && (
          <div className="swipe-counter">
            <IonChip color={swipesRemaining <= 5 ? 'warning' : 'primary'}>
              <IonIcon icon={swipesRemaining <= 5 ? alertCircleOutline : sparkles} />
              <IonLabel>
                {swipesRemaining} swipes remaining today
              </IonLabel>
            </IonChip>
            {swipesRemaining <= 5 && (
              <IonButton
                size="small"
                fill="clear"
                onClick={() => setShowPaywall(true)}
              >
                Get Unlimited
              </IonButton>
            )}
          </div>
        )}

        <IonCard className="match-card" ref={cardRef} key={profile.id}>
          <IonCardHeader>
            <div className="match-header">
              <div className="profile-info" onClick={() => {
                // Only open profile modal if user didn't swipe
                if (!isSwipingRef.current) {
                  setShowProfileModal(true);
                }
              }} style={{ cursor: 'pointer' }}>
                <div className="avatar-container">
                  {profile.avatar ? (
                    <AvatarPreview
                      key={profile.id}
                      avatarData={profile.avatar}
                      size={100}
                      userId={profile.id}
                      isAdmin={profile.is_admin || false}
                    />
                  ) : (
                    <AvatarPreview key={profile.id} size={100} userId={profile.id} isAdmin={profile.is_admin || false} />
                  )}
                </div>
                <div className="text-info">
                  <IonCardTitle className="display-name">
                    {profile.display_name || profile.username}
                  </IonCardTitle>
                  <IonNote>@{profile.username}</IonNote>
                  <IonCardTitle className="match-score">
                    {Math.round(currentMatch.compatibilityScore || 0)}% Match
                  </IonCardTitle>
                </div>
              </div>

              <IonButton fill="clear" onClick={() => setShowActionSheet(true)}>
                <IonIcon icon={ellipsisVertical} slot="icon-only" />
              </IonButton>
            </div>
          </IonCardHeader>

          <IonCardContent>
            {profile.bio && (
              <div className="bio-section">
                <IonText>
                  <p>{profile.bio}</p>
                </IonText>
              </div>
            )}

            {/* Location Section */}
            {(profile.location_name || profile.location_city) && (
              <div className="location-section" style={{ marginTop: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IonIcon icon={locationOutline} style={{ color: 'var(--ion-color-medium)' }} />
                  <IonText style={{ fontSize: '14px', color: 'var(--ion-text-color)' }}>
                    {profile.location_name || `${profile.location_city}${profile.location_state ? `, ${profile.location_state}` : ''}`}
                  </IonText>
                </div>
              </div>
            )}

            {sharedGames.length > 0 && (
              <div className="shared-games-section ion-margin-top">
                <IonList lines="none">
                  <IonListHeader color="medium">
                    <IonLabel>
                      <IonIcon icon={people} slot="start" />
                      {sharedGames.length} Shared Games
                    </IonLabel>
                  </IonListHeader>
                  <div className="chips-container">
                    {sharedGames.slice(0, 5).map(userGame => (
                      <IonChip key={userGame.id} color="secondary">
                        {userGame.game?.name || 'Unknown Game'}
                      </IonChip>
                    ))}
                    {sharedGames.length > 5 && (
                      <IonChip color="light">+ {sharedGames.length - 5} more</IonChip>
                    )}
                  </div>
                </IonList>
              </div>
            )}

            {overlappingSlots.length > 0 && (
              <div className="availability-section ion-margin-top">
                <IonList lines="none">
                  <IonListHeader>
                    <IonLabel>
                      <IonIcon icon={calendar} slot="start" />
                      Overlapping Availability
                    </IonLabel>
                  </IonListHeader>
                  <div className="chips-container">
                    {[...new Set(overlappingSlots.map(s => s.day_of_week))]
                      .slice(0, 3)
                      .map(dayValue => (
                        <IonChip key={dayValue} color="tertiary">
                          {getDayName(dayValue)}
                        </IonChip>
                      ))}
                    {overlappingSlots.length > 3 && (
                      <IonChip color="light">+ {overlappingSlots.length - 3} more</IonChip>
                    )}
                  </div>
                </IonList>
              </div>
            )}
          </IonCardContent>
        </IonCard>

        <div className="action-buttons-container">
          <div className="navigation-buttons">
            <IonButton
              fill="outline"
              shape="round"
              size="large"
              onClick={handlePrevious}
              disabled={isFirst}
            >
              <IonIcon icon={chevronBack} />
            </IonButton>

            <IonButton
              fill="solid"
              shape="round"
              size="large"
              color="danger"
              onClick={handleReject}
              disabled={!currentMatch}
            >
              <IonIcon icon={close} slot="icon-only" style={{ fontSize: '32px' }} />
            </IonButton>

            <IonButton
              fill="solid"
              shape="round"
              size="large"
              onClick={handleSendRequest}
              disabled={sending || matchesLoading || !currentMatch}
              color="success"
            >
              <IonIcon icon={people} slot="icon-only" style={{ fontSize: '32px' }} />
            </IonButton>

            <IonButton
              fill="outline"
              shape="round"
              size="large"
              onClick={handleNext}
              disabled={isLast}
            >
              <IonIcon icon={chevronForward} />
            </IonButton>
          </div>

          {sending && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <IonSpinner name="crescent" />
            </div>
          )}
        </div>

        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header="Options"
          buttons={[
            {
              text: 'View Profile',
              handler: () => setShowProfileModal(true),
            },
            {
              text: 'Report User',
              role: 'destructive',
              handler: () => setShowReportModal(true),
            },
            {
              text: 'Cancel',
              role: 'cancel',
            },
          ]}
        />

        {currentMatch && (
          <ReportModal
            isOpen={showReportModal}
            onDismiss={() => setShowReportModal(false)}
            reportType="user"
            reportedId={currentMatch.profile.id}
            reportedName={currentMatch.profile.username}
            onReportComplete={handleReportComplete}
          />
        )}

        {currentMatch && profile && (
          <UserProfileModal
            isOpen={showProfileModal}
            onDismiss={() => setShowProfileModal(false)}
            userId={currentMatch.profile.id}
            currentUserId={profile.id}
          />
        )}

        <PremiumPaywall
          isOpen={showPaywall}
          onDismiss={() => setShowPaywall(false)}
          feature="swipes"
        />
      </>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Discover</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={segment} onIonChange={e => setSegment(e.detail.value as 'matches' | 'search')}>
            <IonSegmentButton value="matches">
              <IonLabel>Matches</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="search">
              <IonLabel>Find Friends</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh"></IonRefresherContent>
        </IonRefresher>

        {/* Discord Banner */}
        {showDiscordBanner && (
          <div style={{
            backgroundColor: 'var(--ion-color-primary)',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <IonIcon icon={logoDiscord} style={{ fontSize: '24px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
                Join our Discord!
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                Have questions or want to connect with the community?{' '}
                <a
                  href="https://discord.gg/dicecat"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'white',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                >
                  Join here
                </a>
              </div>
            </div>
            <IonButton
              fill="clear"
              onClick={handleDismissDiscordBanner}
              style={{
                '--color': 'white',
                '--padding-start': '8px',
                '--padding-end': '8px',
                margin: 0,
                flexShrink: 0
              }}
            >
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </div>
        )}

        {segment === 'matches' && renderContent()}
        {segment === 'search' && <FindFriends />}
      </IonContent>
    </IonPage>
  );
};

export default Tab1;